import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import './AuthScreen.css';

function AuthScreen({ embedded = false, initialMode = 'signIn' }) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setError('');
    setPendingConfirmation(false);
  };

  const runAuth = async (action) => {
    setError('');
    setSubmitting(true);

    try {
      if (!email || !password) {
        setError('Enter your email and password.');
        return;
      }

      if (action === 'signUp') {
        const { data, error: signUpError } = await signUp(email, password);
        if (signUpError) {
          setError(signUpError.message);
          return;
        }
        if (!data.session) {
          setPendingConfirmation(true);
        }
      } else {
        const { error: signInError } = await signIn(email, password);
        if (signInError) {
          setError(signInError.message);
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAuthButton = async (action) => {
    if (mode !== action) {
      switchMode(action);
      return;
    }
    await runAuth(action);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runAuth(mode);
  };

  const formBlock = (
    <div className={`form-container auth-form-container`}>
      {pendingConfirmation ? (
        <div className="auth-message auth-message--enter">
          Confirm your email to continue.
          <br /><br />
          We sent a confirmation link to <strong>{email}</strong>.
          Open it, then return here to sign in.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={`title-form auth-form auth-form--${mode}`}>
          <div className="auth-billing-tabs" role="tablist" aria-label="Account mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signIn'}
              className={`auth-billing-tab ${mode === 'signIn' ? 'auth-billing-tab--active' : ''}`}
              onClick={() => switchMode('signIn')}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signUp'}
              className={`auth-billing-tab ${mode === 'signUp' ? 'auth-billing-tab--active' : ''}`}
              onClick={() => switchMode('signUp')}
            >
              Sign up
            </button>
          </div>

          <div className="auth-panel" key={mode}>
            <div className="auth-mode-label">
              {mode === 'signUp' ? 'Create an account' : 'Welcome back'}
            </div>
            <label className="auth-field">
              <div className="title-container">Email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label className="auth-field">
              <div className="title-container">Password</div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                minLength={6}
                required
              />
            </label>
            {error && <div className="auth-error">{error}</div>}
            <div className="auth-form-actions">
              <button
                type="button"
                className={`button ${mode === 'signIn' ? 'button-primary' : 'button-secondary'}`}
                disabled={submitting}
                onClick={() => handleAuthButton('signIn')}
              >
                {submitting && mode === 'signIn' ? 'Please wait…' : 'Sign in'}
              </button>
              <button
                type="button"
                className={`button ${mode === 'signUp' ? 'button-primary' : 'button-secondary'}`}
                disabled={submitting}
                onClick={() => handleAuthButton('signUp')}
              >
                {submitting && mode === 'signUp' ? 'Please wait…' : 'Sign up'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="auth-embedded">
        <div className="auth-panel-shell">
          {formBlock}
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-column navbar-column--left" aria-hidden="true" />
        <div className="navbar-column navbar-brand">Ar+</div>
        <div className="navbar-column navbar-column--right">
          {!pendingConfirmation && (
            <>
              <button
                type="button"
                className={`nav-link ${mode === 'signIn' ? 'nav-link--active' : ''}`}
                onClick={() => switchMode('signIn')}
              >
                Sign in
              </button>
              <button
                type="button"
                className={`nav-link ${mode === 'signUp' ? 'nav-link--active' : ''}`}
                onClick={() => switchMode('signUp')}
              >
                Sign up
              </button>
            </>
          )}
        </div>
      </nav>
      <header className="App-header">
        <div className="container container--centered">
          <div className="big-title">
            Web-AR
            <br />
            <span className="title-gradient">For creators.</span>
          </div>
          {formBlock}
        </div>
      </header>
    </div>
  );
}

export default AuthScreen;
